import { ImagePlus, Trash2, UserMinus, UserPlus } from 'lucide-react';
import { useId, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import { Avatar } from '../../components/Avatar';
import { ConfirmDialog, Modal } from '../../components/Modal';
import { errorMessage } from '../../lib/api';
import { dmsApi } from '../../lib/endpoints';
import { paths } from '../../lib/routes';
import { useAppStore } from '../../store/app';
import { overlays } from '../../store/overlays';
import { displayName, dmName, dmParticipants } from '../../store/selectors';
import { toast } from '../../store/ui';
import { ServerIcon } from '../shell/ServerIcon';
import { closeDm } from './dmUtils';
import './dms.css';

/**
 * Gruppen-DM verwalten (Vertrag 3.5): Name und Icon (Besitzer), Mitglieder entfernen (Besitzer),
 * Mitglieder hinzufügen (alle Teilnehmenden), Gruppe verlassen.
 */
export function GroupDmSettings({ channelId, onClose }: { channelId: string; onClose: () => void }) {
  const navigate = useNavigate();
  const { channelId: active } = useParams();
  const ch = useAppStore((s) => s.channels[channelId]);
  const me = useAppStore((s) => s.me);
  const meId = me?.id;
  const maxAvatarBytes = useAppStore((s) => s.limits.maxAvatarBytes);
  const [name, setName] = useState(ch?.name ?? '');
  const [busy, setBusy] = useState(false);
  const [confirm, setConfirm] = useState<{ kind: 'leave' } | { kind: 'remove'; userId: string } | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);
  const id = useId();
  if (!ch || ch.type !== 'GROUP_DM') return null;
  const isOwner = ch.ownerId === meId;
  const recipients = dmParticipants(ch, me);

  const run = async (fn: () => Promise<unknown>, ok?: string) => {
    setBusy(true);
    try {
      await fn();
      if (ok) toast.success(ok);
      return true;
    } catch (e) {
      toast.error('Aktion fehlgeschlagen', errorMessage(e));
      return false;
    } finally {
      setBusy(false);
    }
  };
  const applyChannel = (dto: Awaited<ReturnType<typeof dmsApi.update>>) => useAppStore.getState().dispatch('CHANNEL_UPDATE', dto);

  const saveName = (e: React.FormEvent) => {
    e.preventDefault();
    const value = name.trim();
    void run(async () => applyChannel(await dmsApi.update(ch.id, { name: value || null })), 'Name gespeichert');
  };

  const onIcon = (file: File | undefined) => {
    if (!file) return;
    if (file.size > maxAvatarBytes) {
      toast.error('Datei zu groß', `Höchstens ${Math.round(maxAvatarBytes / 1024 / 1024)} MB.`);
      return;
    }
    void run(async () => applyChannel(await dmsApi.setIcon(ch.id, file)), 'Icon aktualisiert');
  };

  const removeMember = (userId: string) =>
    run(async () => {
      await dmsApi.removeRecipient(ch.id, userId);
      useAppStore.getState().dispatch('CHANNEL_RECIPIENT_REMOVE', { channelId: ch.id, userId });
    }, 'Mitglied entfernt');

  const leave = () =>
    run(async () => {
      await closeDm(ch.id, true);
      onClose();
      if (active === ch.id) void navigate(paths.home());
    }, 'Gruppe verlassen');

  if (confirm?.kind === 'leave') {
    return (
      <ConfirmDialog
          title="Gruppe verlassen"
          message={
            isOwner
              ? 'Du bist Besitzer – der Besitz geht an das älteste Mitglied über. Du kannst nur wieder beitreten, wenn dich jemand hinzufügt.'
              : 'Du kannst nur wieder beitreten, wenn dich jemand hinzufügt.'
          }
          confirmLabel="Verlassen"
          danger
          busy={busy}
          onConfirm={() => void leave().then(() => setConfirm(null))}
          onClose={() => setConfirm(null)}
        />
    );
  }
  if (confirm?.kind === 'remove') {
    return (
      <ConfirmDialog
          title="Mitglied entfernen"
          message={`${displayName(recipients.find((u) => u.id === confirm.userId))} aus der Gruppe entfernen?`}
          confirmLabel="Entfernen"
          danger
          busy={busy}
          onConfirm={() => void removeMember(confirm.userId).then(() => setConfirm(null))}
          onClose={() => setConfirm(null)}
        />
    );
  }

  return (
    <Modal title="Gruppe verwalten" onClose={onClose} testId="group-dm-settings">
      <div className="stack">
        <div className="group-dm-identity">
          <ServerIcon server={{ name: dmName(ch, meId), iconUrl: ch.iconUrl }} size={56} />
          {isOwner && (
            <div className="row">
              <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/gif,image/webp" hidden onChange={(e) => onIcon(e.target.files?.[0])} data-testid="group-dm-icon-input" />
              <button className="btn btn-sm" disabled={busy} onClick={() => fileRef.current?.click()} data-testid="group-dm-icon-upload">
                <ImagePlus size={14} aria-hidden="true" /> Icon ändern
              </button>
              {ch.iconUrl && (
                <button
                  className="btn btn-sm btn-ghost"
                  disabled={busy}
                  onClick={() => void run(async () => applyChannel(await dmsApi.removeIcon(ch.id)), 'Icon entfernt')}
                  data-testid="group-dm-icon-remove"
                >
                  <Trash2 size={14} aria-hidden="true" /> Entfernen
                </button>
              )}
            </div>
          )}
        </div>
        <form className="field" onSubmit={saveName}>
          <label htmlFor={`${id}-name`}>Gruppenname</label>
          <div className="row">
            <input
              id={`${id}-name`}
              className="input"
              value={name}
              maxLength={100}
              disabled={!isOwner}
              placeholder={dmName({ ...ch, name: null }, meId)}
              onChange={(e) => setName(e.target.value)}
              data-testid="group-dm-name"
            />
            {isOwner && (
              <button type="submit" className="btn btn-primary" disabled={busy || name.trim() === (ch.name ?? '')} data-testid="group-dm-save">
                Speichern
              </button>
            )}
          </div>
          {!isOwner && <span className="field-hint">Nur der Besitzer kann Name und Icon ändern.</span>}
        </form>
        <section className="stack" aria-label="Mitglieder">
          <div className="row group-dm-members-head">
            <span className="label">Mitglieder – {recipients.length}</span>
            <span className="spacer" />
            <button
              className="btn btn-sm"
              onClick={() => {
                onClose();
                overlays.open({ type: 'newDm', channelId: ch.id });
              }}
              data-testid="group-dm-add"
            >
              <UserPlus size={14} aria-hidden="true" /> Hinzufügen
            </button>
          </div>
          <ul className="group-dm-members" role="list">
            {recipients.map((u) => (
              <li key={u.id} className="group-dm-member" data-testid="group-dm-member" data-user-id={u.id}>
                <Avatar user={u} size={28} showStatus />
                <span className="truncate">{displayName(u)}</span>
                {u.id === ch.ownerId && <span className="tag">Besitzer</span>}
                {u.id === meId && <span className="small faint">(du)</span>}
                <span className="spacer" />
                {isOwner && u.id !== meId && (
                  <button
                    className="icon-btn icon-btn-xs"
                    disabled={busy}
                    onClick={() => setConfirm({ kind: 'remove', userId: u.id })}
                    aria-label={`${displayName(u)} entfernen`}
                    data-testid="group-dm-member-remove"
                  >
                    <UserMinus size={14} />
                  </button>
                )}
              </li>
            ))}
          </ul>
        </section>
        <div className="row group-dm-footer">
          <button className="btn btn-danger" disabled={busy} onClick={() => setConfirm({ kind: 'leave' })} data-testid="group-dm-leave">
            Gruppe verlassen
          </button>
        </div>
      </div>
    </Modal>
  );
}
